import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { graphql } from 'gatsby';
import qs from 'qs';
import Flickity from 'flickity';
import { Button } from 'chaoskit/src/components';
import { directoryUrl } from 'chaoskit/src/helpers/utility';

import BaseLayout from '../../layouts/BaseLayout';
import Hero from '../../partials/Hero';
import { Data } from '../../components';
import communityMap from '../../assets/media/community-dotted-world-map.svg';
import Talk from '../../partials/community/Talk';
import TutorialTeaser from '../../partials/community/tutorials/TutorialTeaser';

const Community = ({
  data: {
    pageData: { childDataYaml: pageData },
    allTutorials: { edges: tutorialsData },
  },
  location,
}) => {
  const [activeTalk, setActiveTalk] = useState('');

  const talkSlider = useRef();

  const renderFeaturedTutorialsSection = data => (
    <div key={data.category} className="community__tutorialsFeaturedSection">
      <h5 className="u-textMuted">
        <Data>{data.category}</Data>
      </h5>
      <ul className="blockGrid blockGrid-3@medium blockGrid--deepMatch">
        {data.tutorials.map(node => (
          <li key={node.fileAbsolutePath}>
            <TutorialTeaser
              title={node.frontmatter.title}
              link={node.fields.fullUrl}
              author={node.frontmatter.author}
              authorUrl={node.fields.authorUrl}
              excerpt={node.excerpt}
              categories={node.frontmatter.categories}
            />
          </li>
        ))}
      </ul>
    </div>
  );

  const getFeaturedTutorials = () => {
    // Get node data out of tutorials query
    const tutorials = tutorialsData.reduce(
      (accumulator, currentValue) => accumulator.concat(currentValue.node),
      []
    );
    const matchedTutorialsGroup = [];

    pageData.intro.featuredTutorials.map(featuredTutorialsSection => {
      // Grab only interior title from featured tutorials data
      const featuredTutorials = featuredTutorialsSection.items.reduce(
        (accumulator, currentValue) => accumulator.concat(currentValue.title),
        []
      );

      const matchedTutorials = [];

      // Attempt to match featured tutorial item (from yaml data) against queried data and return full node
      tutorials.map(tutorial => {
        if (featuredTutorials.includes(tutorial.frontmatter.title)) {
          matchedTutorials.push(tutorial);
        }

        return false;
      });

      if (matchedTutorials.length) {
        matchedTutorialsGroup.push({
          category: featuredTutorialsSection.category,
          tutorials: matchedTutorials,
        });
      }

      return true;
    });

    return matchedTutorialsGroup;
  };

  const attachTalkSlider = () => {
    const flickity = new Flickity(talkSlider.current, {
      initialIndex: Math.ceil(3 / 2) - 1,
      prevNextButtons: false,
      pageDots: false,
    });

    // On static click of cell; set active talk
    flickity.on('staticClick', (event, pointer, cellElement, cellIndex) => {
      setActiveTalk(pageData.talks.videos[cellIndex].youtubeId);
    });
  };

  useEffect(() => {
    attachTalkSlider();

    // If page contains `video=XXXX` param, attempt to set `activeTalk` to open up modal
    const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });

    if (queryParams.video) {
      if (
        pageData.talks.videos.find(
          video => queryParams.video === video.youtubeId
        )
      ) {
        setActiveTalk(queryParams.video);
      }
    }
  }, []);

  const handleCloseTalkModal = () => {
    setActiveTalk('');
  };

  return (
    <BaseLayout headerProps={{ community: true }}>
      <Helmet
        title={pageData.title}
        meta={[{ name: 'description', content: pageData.description }]}
      />
      <Hero compact particles={false} className="community__hero">
        <div className="hero-content">
          <div className="container">
            <div className="hero-titleWrapper">
              <h1 className="hero-title">
                <Data>{pageData.hero.heading}</Data>
              </h1>
              <div className="hero-titleSub">
                <Data markdown>{pageData.hero.blurb}</Data>
              </div>
            </div>
          </div>
        </div>
      </Hero>
      <section className="section section--large section--shadow section--slant">
        <div className="container">
          <div className="row">
            <div className="column-5@medium">
              <div className="section-titleWrapper section-titleWrapper--forceLeft@medium">
                <h3 className="section-title">
                  <Data>{pageData.intro.heading}</Data>
                </h3>
                <div className="section-titleSub">
                  <Data markdown>{pageData.intro.blurb}</Data>
                </div>
              </div>
            </div>
          </div>
          {getFeaturedTutorials().length
            ? getFeaturedTutorials().map(featuredTutorialSection =>
                renderFeaturedTutorialsSection(featuredTutorialSection)
              )
            : null}
          <div className="u-mt--xlarge u-textCenter">
            <Button type="outlinePrimary" url="/community/tutorials/">
              View all
            </Button>
          </div>
        </div>
      </section>
      <section className="section section--large section--shadow section--slant">
        <div className="container">
          <div className="section-titleWrapper">
            <h3 className="section-title">
              <Data>{pageData.map.heading}</Data>
            </h3>
            <div className="section-titleSub">
              <Data markdown>{pageData.map.blurb}</Data>
            </div>
          </div>
          <img src={communityMap} alt="Meetups" />
          <div className="u-textCenter u-mt--xlarge">
            <Button
              type="outlinePrimary"
              url={directoryUrl(pageData.map.cta.url)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Data>{pageData.map.cta.label}</Data>
            </Button>
          </div>
        </div>
      </section>
      {pageData.talks.videos.length > 0 && (
        <section id="talks" className="section has-anchorBump">
          <div className="container">
            <div className="row">
              <div className="column-5@medium">
                <div className="section-titleWrapper section-titleWrapper--forceLeft@medium">
                  <h3 className="section-title">
                    <Data>{pageData.talks.heading}</Data>
                  </h3>
                  <div className="section-titleSub">
                    <Data markdown>{pageData.talks.blurb}</Data>
                  </div>
                </div>
              </div>
            </div>
            <div className="community__talks-wrapper u-contrast">
              <div className="community__talks-slider" ref={talkSlider}>
                {pageData.talks.videos.map(talk => (
                  <Talk
                    data={talk}
                    key={talk.youtubeId}
                    activeTalk={activeTalk}
                    handleCloseTalkModal={handleCloseTalkModal}
                    authorComponents={['avatar', 'name']}
                    location={location}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </BaseLayout>
  );
};

Community.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object.isRequired,
};

export default Community;

export const pageQuery = graphql`
  query CommunityPageData {
    pageData: file(name: { eq: "community" }) {
      childDataYaml {
        title
        description
        hero {
          blurb
          heading
        }
        intro {
          blurb
          heading
          featuredTutorials {
            category
            items {
              title
            }
          }
        }
        map {
          blurb
          cta {
            label
            url
            newTab
          }
          heading
        }
        talks {
          blurb
          heading
          videos {
            presenter
            title
            youtubeId
          }
        }
      }
    }

    allTutorials: allMarkdownRemark(
      filter: {
        # Make sure we're only querying tutorials
        fileAbsolutePath: { regex: "/src/tutorials/" }
        frontmatter: { title: { ne: "BLUEPRINT" } }
      }
    ) {
      edges {
        node {
          ...TutorialTeaserFragment
        }
      }
    }
  }
`;
